import {useReducer, useCallback, useEffect, useMemo} from 'react';
import nextId from 'react-id-generator';
import {TKeyResult} from './interface';

interface IResult {
  keyresult: TKeyResult;
  valid: [];
}

function addNewKeyresult(
  keyresults: Map<string, IResult>,
): Map<string, IResult> {
  const nextKeyresults = new Map(keyresults);
  nextKeyresults.set(nextId('keyresult-id'), {
    keyresult: {
      id: '',
      measurement: {unit: '', symbol: ''},
      name: '',
      range: {start: undefined, target: undefined, current: undefined},
      user: '',
      showSource: false,
      saveSource: false,
      source: null,
    },
    valid: [],
  });

  return nextKeyresults;
}

function defaultEmails(keyresults?: TKeyResult[]): Map<string, IResult> {
  return new Map(
    (keyresults || []).map((keyresult) => [
      nextId('keyresult-id'),
      {keyresult, valid: []},
    ]),
  );
}

function removeKeyresult(
  keyresults: Map<string, IResult>,
  key: string,
): Map<string, IResult> {
  const nextKeyresults = new Map(keyresults);
  nextKeyresults.delete(key);
  return nextKeyresults;
}

const updateKeyresult = (
  keyresults: Map<string, IResult>,
  key: string,
  keyresult: TKeyResult,
): Map<string, IResult> => {
  const nextKeyresults = new Map(keyresults);
  nextKeyresults.set(key, {
    keyresult,
    valid: [],
  });

  return nextKeyresults;
};

interface ReducerState {
  keyresults: Map<string, IResult>;
}

export enum ActionType {
  REMOVE_KEYRESULT,
  ADD_KEYRESULT,
  UPDATE_KEYRESULT,
}

type AddNewEmailAction = {type: ActionType.ADD_KEYRESULT};

type UpdateEmailAction = {
  type: ActionType.UPDATE_KEYRESULT;
  payload: {value: TKeyResult; key: string};
};
type RemoveKeyResultsAction = {
  type: ActionType.REMOVE_KEYRESULT;
  payload: {key: string};
};

type ReducerAction =
  | AddNewEmailAction
  | UpdateEmailAction
  | RemoveKeyResultsAction;

const initialState: ReducerState = {
  keyresults: new Map(),
};

function keyresultsReducer(
  state: ReducerState,
  action: ReducerAction,
): ReducerState {
  switch (action.type) {
    case ActionType.ADD_KEYRESULT:
      return {
        keyresults: addNewKeyresult(state.keyresults),
      };

    case ActionType.UPDATE_KEYRESULT:
      return {
        keyresults: updateKeyresult(
          state.keyresults,
          action.payload.key,
          action.payload.value,
        ),
      };
    case ActionType.REMOVE_KEYRESULT:
      return {
        keyresults: removeKeyresult(state.keyresults, action.payload.key),
      };
    default:
      return state;
  }
}

export function useKeyResult(
  keyresults?: TKeyResult[],
  setValue?: (values: TKeyResult[]) => void,
) {
  const [state, dispatch] = useReducer(keyresultsReducer, {
    ...initialState,
    keyresults: defaultEmails(keyresults),
  });

  useEffect(() => {
    if (setValue) {
      setValue(
        Array.from(state.keyresults.values()).map(({keyresult}) => keyresult),
      );
    }
  }, [setValue, state.keyresults]);

  const addKeyresult = useCallback(() => {
    dispatch({type: ActionType.ADD_KEYRESULT});
  }, []);

  const removeKeyresult = useCallback((key: string) => {
    dispatch({type: ActionType.REMOVE_KEYRESULT, payload: {key}});
  }, []);

  const onChangeKeyResult = useCallback(
    (key: string, keyresult: TKeyResult) => {
      dispatch({
        type: ActionType.UPDATE_KEYRESULT,
        payload: {value: keyresult, key},
      });
    },
    [],
  );

  const disabled = useMemo(() => {
    return state.keyresults.size >= 5;
  }, [state.keyresults.size]);

  return {
    keyresults: state.keyresults,
    addKeyresult,
    removeKeyresult,
    onChangeKeyResult,
    disabled,
  };
}
